<script setup>
    // Component
    import BaseInput from '@/components/BaseInputComponent.vue';
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useSetupAgreeStore } from '@/stores/setup/setupAgreeStore';
    import { useHeaderStore } from '@/stores/header/headerStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useSetupCompanyStore } from '@/stores/setup/setupCompanyStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';

    // Vue
    import { getCurrentInstance, inject, onMounted, reactive } from 'vue';
    import { useRouter } from 'vue-router';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();

    const setupAgreeStore = useSetupAgreeStore();

    const headerStore = useHeaderStore();

    const loginStore = useLoginStore();

    const setupCompanyStore = useSetupCompanyStore();

    const A = {
        CompanyPwd: '',
    };

    const setupForm = reactive(A);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    onMounted(async () => {});

    async function checkForm() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // ユーザー企業PW確認APIをコールして、ユーザーを登録する。
        const response = await setupCompanyStore.checkUserCompanyPasswordAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, setupForm);

        if (response.success) {
            if (response.responseBody.ApiRes.Status === 0) {
                // ユーザー状態確認(ログイン)APIをコールして、ユーザーの各種状態を取得する。
                const response2 = await loginStore.getUserStatusByLoginAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, null);

                if (response2.success) {
                    // 有効な契約が存在するか判定する。
                    // 有効化契約が存在しない場合は、エラーメッセージを表示して、画面に留まる。
                    if (response2.responseBody.ContractAssigned) {
                        // 利用規定同意要否を判定する。
                        if (response2.responseBody.RegulationNeed2Agree) {
                            // 規定PKを保持する。
                            setupAgreeStore.setRegulationPk(response2.responseBody.RegulationPk);

                            // 利用規定同意画面へ遷移する。
                            router.push({ name: 'setupAgree' });
                        } else if (response2.responseBody.UserInfoNeed2Update) {
                            // ユーザー情報更新要否を判定する。

                            // ユーザー情報入力画面へ遷移する。
                            router.push({ name: 'setupUser' });
                        } else if (response2.responseBody.ParentAgreeNeed2Check) {
                            // 親権者同意確認要否を判定する。

                            // 親権者同意画面へ遷移する。
                            router.push({ name: 'setupParent' });
                        } else {
                            // 上記のいずれも不要、もしくは、すでに同意済み等で設定が不要な場合。
                            // ホーム画面へ遷移する。
                            router.push({ name: 'home' });
                        }
                    } else {
                        // 有効な契約が存在しない場合はエラーにする。
                        messageDialog('error', i18n.global.t('common.message.isContractAssignError'));

                        // ログアウト処理を実行する。
                        const logoutUrl = await headerStore.logoutAction();

                        // ログアウトの遷移先に応じて移動する。
                        if (logoutUrl.href) {
                            location.href = logoutUrl.url;
                        } else {
                            router.push({ name: logoutUrl.url, query: logoutUrl.query });
                        }
                    }
                } else {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return;
                }
            } else {
                // 企業パスワードが不正の場合。
                messageDialog('error', i18n.global.t('setupCompany.message.message3'));
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 企業パスワード
        if (Utils.isNullEmpty(setupForm.CompanyPwd)) {
            message += i18n.global.t('setupCompany.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(setupForm.CompanyPwd)) {
                message += i18n.global.t('setupCompany.message.message2') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberMix(setupForm.CompanyPwd)) {
                message += i18n.global.t('setupCompany.message.message3') + '\n';
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }
</script>

<style></style>

<template>
    <section id="main" class="bg login extra">
        <MainHeader />
        <div id="contents" class="small">
            <h2>{{ $t('setupCompany.parts.pageTitle1') }}</h2>
            <section class="form short">
                <dl>
                    <dt>
                        <span>{{ $t('setupCompany.parts.dl1') }}</span>
                    </dt>
                    <dd>
                        <BaseInput v-model:input="setupForm.CompanyPwd" type="password" />
                    </dd>
                </dl>
            </section>
            <ul class="btnArea">
                <li>
                    <button type="button" class="btnPrimary" @click="checkForm" :disabled="disabledStore.isDisabled">
                        {{ $t('setupCompany.parts.button1') }}
                    </button>
                </li>
            </ul>
        </div>
    </section>
</template>
